export interface SkillEditorSourceFile {
    readonly id: number;
    readonly name: string;
    readonly content: string;
    readonly updated_at: string;
}

export interface SkillEditorSource {
    readonly id: number;
    readonly name: string;
    readonly description: string;
    readonly content: string;
    readonly organization_id: number | null;
    readonly organization_group_id?: number | null;
    readonly is_core: boolean;
    readonly updated_at: string;
    readonly claude_skill_id?: string | null;
    readonly published_at?: string | null;
    readonly skill_files: readonly SkillEditorSourceFile[];
}

export type SkillEditorScopeKind = "global" | "organization" | "group";

export interface SkillEditorFields {
    readonly name: string;
    readonly description: string;
    readonly content: string;
    readonly organizationId: number | null;
    readonly organizationGroupId: number | null;
    readonly scopeKind: SkillEditorScopeKind;
    readonly isCore: boolean;
}

export interface SkillEditorFile {
    readonly clientKey: string;
    readonly serverId: number | null;
    readonly name: string;
    readonly content: string;
    readonly createdOrder: number;
    readonly isPlaceholder: boolean;
    readonly renameOrigin: string | null;
    readonly acknowledgedRename: string | null;
}

export interface SkillEditorBaselineSkill extends SkillEditorFields {
    readonly skillId: number;
    readonly updatedAt: string;
    readonly claudeSkillId: string | null;
    readonly publishedAt: string | null;
}

export interface SkillEditorBaselineFile {
    readonly clientKey: string;
    readonly serverId: number;
    readonly name: string;
    readonly content: string;
    readonly updatedAt: string;
    readonly displayName: string;
}

export interface SkillEditorBaseline {
    readonly skill: SkillEditorBaselineSkill;
    readonly files: readonly SkillEditorBaselineFile[];
    readonly seededServerIds: readonly number[];
}

export interface SkillEditorBuffer {
    readonly skill: SkillEditorFields;
    readonly files: readonly SkillEditorFile[];
}

export interface SkillEditorState {
    readonly skillId: number;
    readonly baseline: SkillEditorBaseline;
    readonly buffer: SkillEditorBuffer;
    readonly selectedClientKey: string;
    readonly resetEpoch: number;
    readonly reviewRequired: boolean;
    readonly editGeneration: number;
    readonly nextLocalFileKey: number;
    readonly renameRequest: SkillEditorRenameRequest | null;
}

export interface SkillEditorRenameRequest {
    readonly clientKey: string;
    readonly origin: string;
    readonly destination: string;
    readonly referenceCount: number;
}

export interface CreateSkillEditorStateOptions {
    readonly reviewRequired?: boolean;
}

export interface ResetSkillEditorStateOptions {
    readonly reviewRequired?: boolean;
}

export type SkillEditorSkillField = keyof SkillEditorFields;
export type SkillEditorFileField = "name" | "content";

export interface SkillEditorUpdatedFileDiff {
    readonly file: SkillEditorFile;
    readonly baseline: SkillEditorBaselineFile;
    readonly changedFields: readonly SkillEditorFileField[];
}

export interface SkillEditorDiff {
    readonly skillFields: readonly SkillEditorSkillField[];
    readonly deletedFiles: readonly SkillEditorBaselineFile[];
    readonly updatedFiles: readonly SkillEditorUpdatedFileDiff[];
    readonly addedFiles: readonly SkillEditorFile[];
    readonly orderingDependentRenames: readonly SkillEditorUpdatedFileDiff[];
    readonly hasPersistedChanges: boolean;
    readonly hasVersionedChanges: boolean;
}

export const SKILL_INDEX_CLIENT_KEY = "index";
export const normalizeSkillEditorText = (value: string) => value.trim();
export const isSkillEditorNameNormalizationRepair = (
    name: string,
    baselineName: string
) =>
    name === normalizeSkillEditorText(name) &&
    baselineName !== normalizeSkillEditorText(baselineName) &&
    normalizeSkillEditorText(name) === normalizeSkillEditorText(baselineName);

const SKILL_EDITOR_FIELD_ORDER: readonly SkillEditorSkillField[] = [
    "name",
    "description",
    "content",
    "scopeKind",
    "organizationId",
    "organizationGroupId",
    "isCore",
];
const VERSIONED_SKILL_FIELDS = new Set<SkillEditorSkillField>([
    "name",
    "description",
    "content",
]);

export type SkillEditorFieldGroup = "index" | "settings";

// Exhaustive by construction: a new SkillEditorFields entry fails to compile
// until it is grouped, rather than silently dropping out of the save summary.
export const SKILL_EDITOR_FIELD_GROUP: Record<
    SkillEditorSkillField,
    SkillEditorFieldGroup
> = {
    content: "index",
    description: "index",
    name: "settings",
    scopeKind: "settings",
    organizationId: "settings",
    organizationGroupId: "settings",
    isCore: "settings",
};

export const isSkillIndexField = (field: SkillEditorSkillField): boolean =>
    SKILL_EDITOR_FIELD_GROUP[field] === "index";

export const isSkillSettingsField = (field: SkillEditorSkillField): boolean =>
    SKILL_EDITOR_FIELD_GROUP[field] === "settings";

const clientKeyForServerFile = (serverId: number) => `file:${serverId}`;

export const skillEditorFieldsFromSource = (
    source: SkillEditorSource
): SkillEditorFields => ({
    name: source.name,
    description: source.description,
    content: source.content,
    organizationId: source.organization_id,
    organizationGroupId: source.organization_group_id ?? null,
    scopeKind:
        source.organization_group_id != null
            ? "group"
            : source.organization_id != null
              ? "organization"
              : "global",
    isCore: source.is_core,
});

export const getSkillEditorScopeAssignment = (
    fields: Pick<
        SkillEditorFields,
        "scopeKind" | "organizationId" | "organizationGroupId"
    >
) => {
    switch (fields.scopeKind) {
        case "global":
            return { organizationId: null, organizationGroupId: null };
        case "organization":
            return {
                organizationId: fields.organizationId,
                organizationGroupId: null,
            };
        case "group":
            return {
                organizationId: null,
                organizationGroupId: fields.organizationGroupId,
            };
    }
};

export const skillEditorScopesEqual = (
    left: Pick<
        SkillEditorFields,
        "scopeKind" | "organizationId" | "organizationGroupId"
    >,
    right: Pick<
        SkillEditorFields,
        "scopeKind" | "organizationId" | "organizationGroupId"
    >
) => {
    if (left.scopeKind !== right.scopeKind) return false;
    const leftAssignment = getSkillEditorScopeAssignment(left);
    const rightAssignment = getSkillEditorScopeAssignment(right);
    return (
        leftAssignment.organizationId === rightAssignment.organizationId &&
        leftAssignment.organizationGroupId ===
            rightAssignment.organizationGroupId
    );
};

export const isSkillEditorScopeDirty = (
    buffer: SkillEditorFields,
    baseline: SkillEditorFields
) => !skillEditorScopesEqual(buffer, baseline);

const baselineFromSource = (source: SkillEditorSource): SkillEditorBaseline => {
    const files = source.skill_files.map((file) => ({
        clientKey: clientKeyForServerFile(file.id),
        serverId: file.id,
        name: file.name,
        content: file.content,
        updatedAt: file.updated_at,
        displayName: file.name,
    }));

    return {
        skill: {
            skillId: source.id,
            ...skillEditorFieldsFromSource(source),
            updatedAt: source.updated_at,
            claudeSkillId: source.claude_skill_id ?? null,
            publishedAt: source.published_at ?? null,
        },
        files,
        seededServerIds: files.map(({ serverId }) => serverId),
    };
};

const filesFromSource = (source: SkillEditorSource): SkillEditorFile[] =>
    source.skill_files.map((file, createdOrder) => ({
        clientKey: clientKeyForServerFile(file.id),
        serverId: file.id,
        name: file.name,
        content: file.content,
        createdOrder,
        isPlaceholder: false,
        renameOrigin: null,
        acknowledgedRename: null,
    }));

const selectedContent = (state: SkillEditorState): string => {
    if (state.selectedClientKey === SKILL_INDEX_CLIENT_KEY) {
        return state.buffer.skill.content;
    }

    return (
        state.buffer.files.find(
            ({ clientKey }) => clientKey === state.selectedClientKey
        )?.content ?? ""
    );
};

const stateContainsClientKey = (
    state: SkillEditorState,
    clientKey: string
): boolean =>
    clientKey === SKILL_INDEX_CLIENT_KEY ||
    state.buffer.files.some((file) => file.clientKey === clientKey);

const compareText = (left: string, right: string): number => {
    if (left < right) return -1;
    if (left > right) return 1;
    return 0;
};

const compareCaseInsensitive = (left: string, right: string): number =>
    compareText(left.toLowerCase(), right.toLowerCase());

const baselineSource = (state: SkillEditorState): SkillEditorSource => ({
    id: state.skillId,
    name: state.baseline.skill.name,
    description: state.baseline.skill.description,
    content: state.baseline.skill.content,
    organization_id: getSkillEditorScopeAssignment(state.baseline.skill)
        .organizationId,
    organization_group_id: getSkillEditorScopeAssignment(state.baseline.skill)
        .organizationGroupId,
    is_core: state.baseline.skill.isCore,
    updated_at: state.baseline.skill.updatedAt,
    claude_skill_id: state.baseline.skill.claudeSkillId,
    published_at: state.baseline.skill.publishedAt,
    skill_files: state.baseline.files.map((file) => ({
        id: file.serverId,
        name: file.name,
        content: file.content,
        updated_at: file.updatedAt,
    })),
});

const skillFieldChanged = (
    buffer: SkillEditorFields,
    baseline: SkillEditorFields,
    field: SkillEditorSkillField
): boolean => {
    if (field === "name" || field === "description") {
        return (
            normalizeSkillEditorText(buffer[field]) !==
            normalizeSkillEditorText(baseline[field])
        );
    }
    if (field === "organizationId") {
        return (
            getSkillEditorScopeAssignment(buffer).organizationId !==
            getSkillEditorScopeAssignment(baseline).organizationId
        );
    }
    if (field === "organizationGroupId") {
        return (
            getSkillEditorScopeAssignment(buffer).organizationGroupId !==
            getSkillEditorScopeAssignment(baseline).organizationGroupId
        );
    }
    return buffer[field] !== baseline[field];
};

export function deriveSkillEditorDiff(
    state: SkillEditorState
): SkillEditorDiff {
    const skillFields = SKILL_EDITOR_FIELD_ORDER.filter((field) =>
        skillFieldChanged(state.buffer.skill, state.baseline.skill, field)
    );
    const bufferByClientKey = new Map(
        state.buffer.files.map((file) => [file.clientKey, file])
    );
    const baselineByClientKey = new Map(
        state.baseline.files.map((file) => [file.clientKey, file])
    );
    const seededServerIds = new Set(state.baseline.seededServerIds);
    const deletedFiles = state.baseline.files.filter(
        (file) =>
            seededServerIds.has(file.serverId) &&
            !bufferByClientKey.has(file.clientKey)
    );
    const updatedFiles = state.buffer.files.flatMap((file) => {
        const baseline = baselineByClientKey.get(file.clientKey);
        if (!baseline) return [];

        const changedFields: SkillEditorFileField[] = [];
        if (
            normalizeSkillEditorText(file.name) !==
                normalizeSkillEditorText(baseline.name) ||
            isSkillEditorNameNormalizationRepair(file.name, baseline.name)
        ) {
            changedFields.push("name");
        }
        if (file.content !== baseline.content) {
            changedFields.push("content");
        }
        return changedFields.length === 0
            ? []
            : [{ file, baseline, changedFields }];
    });
    const addedFiles = state.buffer.files.filter(
        (file) => file.serverId === null && !file.isPlaceholder
    );
    const remainingBaselineFiles = state.baseline.files.filter((file) =>
        bufferByClientKey.has(file.clientKey)
    );
    const orderingDependentRenames = updatedFiles.filter(
        ({ file, baseline }) =>
            normalizeSkillEditorText(file.name) !==
                normalizeSkillEditorText(baseline.name) &&
            remainingBaselineFiles.some(
                (candidate) =>
                    candidate.clientKey !== file.clientKey &&
                    normalizeSkillEditorText(candidate.name).toLowerCase() ===
                        normalizeSkillEditorText(file.name).toLowerCase()
            )
    );
    const hasFileChanges =
        deletedFiles.length > 0 ||
        updatedFiles.length > 0 ||
        addedFiles.length > 0;

    return {
        skillFields,
        deletedFiles,
        updatedFiles,
        addedFiles,
        orderingDependentRenames,
        hasPersistedChanges: skillFields.length > 0 || hasFileChanges,
        hasVersionedChanges:
            skillFields.some((field) => VERSIONED_SKILL_FIELDS.has(field)) ||
            hasFileChanges,
    };
}

export function isSkillEditorDirty(state: SkillEditorState): boolean {
    return (
        state.reviewRequired || deriveSkillEditorDiff(state).hasPersistedChanges
    );
}

export function isSkillEditorFileDirty(
    state: SkillEditorState,
    clientKey: string
): boolean {
    if (clientKey === SKILL_INDEX_CLIENT_KEY) {
        return (
            skillFieldChanged(
                state.buffer.skill,
                state.baseline.skill,
                "content"
            ) ||
            skillFieldChanged(
                state.buffer.skill,
                state.baseline.skill,
                "description"
            )
        );
    }

    const file = state.buffer.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    if (!file) return false;
    if (file.serverId === null) return !file.isPlaceholder;

    const baseline = state.baseline.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    return (
        baseline !== undefined &&
        (normalizeSkillEditorText(file.name) !==
            normalizeSkillEditorText(baseline.name) ||
            isSkillEditorNameNormalizationRepair(file.name, baseline.name) ||
            file.content !== baseline.content)
    );
}

export function createSkillEditorState(
    source: SkillEditorSource,
    options: CreateSkillEditorStateOptions = {}
): SkillEditorState {
    return {
        skillId: source.id,
        baseline: baselineFromSource(source),
        buffer: {
            skill: skillEditorFieldsFromSource(source),
            files: filesFromSource(source),
        },
        selectedClientKey: SKILL_INDEX_CLIENT_KEY,
        resetEpoch: 0,
        reviewRequired: options.reviewRequired ?? false,
        editGeneration: 0,
        nextLocalFileKey: 1,
        renameRequest: null,
    };
}

export function updateSkillEditorFields(
    state: SkillEditorState,
    patch: Partial<SkillEditorFields>
): SkillEditorState {
    const current = state.buffer.skill;
    const next: SkillEditorFields = {
        name: patch.name === undefined ? current.name : patch.name,
        description:
            patch.description === undefined
                ? current.description
                : patch.description,
        content: patch.content === undefined ? current.content : patch.content,
        organizationId:
            patch.organizationId === undefined
                ? current.organizationId
                : patch.organizationId,
        organizationGroupId:
            patch.organizationGroupId === undefined
                ? current.organizationGroupId
                : patch.organizationGroupId,
        scopeKind:
            patch.scopeKind === undefined ? current.scopeKind : patch.scopeKind,
        isCore: patch.isCore === undefined ? current.isCore : patch.isCore,
    };

    if (
        SKILL_EDITOR_FIELD_ORDER.every(
            (field) => current[field] === next[field]
        )
    ) {
        return state;
    }

    return {
        ...state,
        buffer: { ...state.buffer, skill: next },
        editGeneration: state.editGeneration + 1,
    };
}

export function addSkillEditorFile(state: SkillEditorState): SkillEditorState {
    const placeholder = state.buffer.files.find(
        ({ isPlaceholder }) => isPlaceholder
    );
    if (placeholder) {
        return selectSkillEditorFile(state, placeholder.clientKey);
    }

    const clientKey = `new:${state.nextLocalFileKey}`;
    const file: SkillEditorFile = {
        clientKey,
        serverId: null,
        name: "",
        content: "",
        createdOrder: state.baseline.files.length + state.nextLocalFileKey - 1,
        isPlaceholder: true,
        renameOrigin: null,
        acknowledgedRename: null,
    };

    return {
        ...state,
        buffer: {
            ...state.buffer,
            files: [...state.buffer.files, file],
        },
        selectedClientKey: clientKey,
        editGeneration: state.editGeneration + 1,
        nextLocalFileKey: state.nextLocalFileKey + 1,
    };
}

export function updateSkillEditorFile(
    state: SkillEditorState,
    clientKey: string,
    patch: Partial<Pick<SkillEditorFile, "name" | "content">>
): SkillEditorState {
    const index = state.buffer.files.findIndex(
        (file) => file.clientKey === clientKey
    );
    if (index === -1) return state;

    const current = state.buffer.files[index];
    const name = patch.name === undefined ? current.name : patch.name;
    const content =
        patch.content === undefined ? current.content : patch.content;
    if (name === current.name && content === current.content) return state;

    const nameChanged = name !== current.name;
    const baseline = state.baseline.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    const isPersistedRename =
        nameChanged &&
        current.serverId !== null &&
        baseline !== undefined &&
        normalizeSkillEditorText(name) !==
            normalizeSkillEditorText(baseline.name);
    const renameOrigin = nameChanged
        ? isPersistedRename
            ? (current.renameOrigin ?? baseline?.name ?? current.name)
            : null
        : current.renameOrigin;
    const acknowledgedRename = nameChanged ? null : current.acknowledgedRename;

    const files = [...state.buffer.files];
    files[index] = {
        ...current,
        name,
        content,
        isPlaceholder: false,
        renameOrigin,
        acknowledgedRename,
    };

    return {
        ...state,
        buffer: { ...state.buffer, files },
        editGeneration: state.editGeneration + 1,
        renameRequest:
            nameChanged && state.renameRequest?.clientKey === clientKey
                ? null
                : state.renameRequest,
    };
}

export function selectSkillEditorFile(
    state: SkillEditorState,
    clientKey: string
): SkillEditorState {
    if (!stateContainsClientKey(state, clientKey)) return state;
    if (state.selectedClientKey === clientKey) return state;

    const files = state.buffer.files.filter(
        (file) =>
            file.clientKey !== state.selectedClientKey || !file.isPlaceholder
    );

    return {
        ...state,
        buffer:
            files.length === state.buffer.files.length
                ? state.buffer
                : { ...state.buffer, files },
        selectedClientKey: clientKey,
    };
}

export function getSkillEditorDisplayFiles(
    state: SkillEditorState
): readonly SkillEditorFile[] {
    const baselineByClientKey = new Map(
        state.baseline.files.map((file) => [file.clientKey, file])
    );

    return [...state.buffer.files].sort((left, right) => {
        const leftGroup =
            left.serverId !== null ? 0 : left.isPlaceholder ? 2 : 1;
        const rightGroup =
            right.serverId !== null ? 0 : right.isPlaceholder ? 2 : 1;
        if (leftGroup !== rightGroup) return leftGroup - rightGroup;

        if (leftGroup === 0) {
            const leftName =
                baselineByClientKey.get(left.clientKey)?.displayName ??
                left.name;
            const rightName =
                baselineByClientKey.get(right.clientKey)?.displayName ??
                right.name;
            const byName = compareCaseInsensitive(leftName, rightName);
            if (byName !== 0) return byName;
            return compareText(left.clientKey, right.clientKey);
        }

        if (left.createdOrder !== right.createdOrder) {
            return left.createdOrder - right.createdOrder;
        }
        return compareText(left.clientKey, right.clientKey);
    });
}

export function requestSkillEditorRenameCommit(
    state: SkillEditorState,
    clientKey: string,
    referenceCount: number
): SkillEditorState {
    const file = state.buffer.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    if (!file || file.serverId === null || file.renameOrigin === null) {
        return state;
    }
    if (normalizeSkillEditorText(file.name).length === 0) return state;
    if (file.acknowledgedRename === file.name) return state;

    if (referenceCount === 0) {
        return {
            ...state,
            buffer: {
                ...state.buffer,
                files: state.buffer.files.map((candidate) =>
                    candidate.clientKey === clientKey
                        ? { ...candidate, acknowledgedRename: candidate.name }
                        : candidate
                ),
            },
            renameRequest: null,
        };
    }

    const renameRequest = {
        clientKey,
        origin: file.renameOrigin,
        destination: file.name,
        referenceCount,
    };
    if (
        state.renameRequest?.clientKey === renameRequest.clientKey &&
        state.renameRequest.origin === renameRequest.origin &&
        state.renameRequest.destination === renameRequest.destination &&
        state.renameRequest.referenceCount === renameRequest.referenceCount
    ) {
        return state;
    }
    return { ...state, renameRequest };
}

export function acknowledgeSkillEditorRename(
    state: SkillEditorState,
    clientKey: string,
    destination: string
): SkillEditorState {
    const request = state.renameRequest;
    if (
        request?.clientKey !== clientKey ||
        request.destination !== destination
    ) {
        return state;
    }
    const file = state.buffer.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    if (!file || file.renameOrigin === null || file.name !== destination) {
        return state;
    }

    return {
        ...state,
        buffer: {
            ...state.buffer,
            files: state.buffer.files.map((candidate) =>
                candidate.clientKey === clientKey
                    ? { ...candidate, acknowledgedRename: destination }
                    : candidate
            ),
        },
        renameRequest: null,
    };
}

export function cancelSkillEditorRename(
    state: SkillEditorState,
    clientKey: string
): SkillEditorState {
    const file = state.buffer.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    if (!file || file.renameOrigin === null) return state;

    return {
        ...state,
        buffer: {
            ...state.buffer,
            files: state.buffer.files.map((candidate) =>
                candidate.clientKey === clientKey
                    ? {
                          ...candidate,
                          name: file.renameOrigin ?? candidate.name,
                          renameOrigin: null,
                          acknowledgedRename: null,
                      }
                    : candidate
            ),
        },
        editGeneration: state.editGeneration + 1,
        renameRequest:
            state.renameRequest?.clientKey === clientKey
                ? null
                : state.renameRequest,
    };
}

export function deleteSkillEditorFile(
    state: SkillEditorState,
    clientKey: string
): SkillEditorState {
    const file = state.buffer.files.find(
        (candidate) => candidate.clientKey === clientKey
    );
    if (!file) return state;

    const displayFiles = getSkillEditorDisplayFiles(state);
    const displayIndex = displayFiles.findIndex(
        (candidate) => candidate.clientKey === clientKey
    );
    const files = state.buffer.files.filter(
        (candidate) => candidate.clientKey !== clientKey
    );
    const withoutFile: SkillEditorState = {
        ...state,
        buffer: { ...state.buffer, files },
        editGeneration: state.editGeneration + (file.isPlaceholder ? 0 : 1),
        renameRequest:
            state.renameRequest?.clientKey === clientKey
                ? null
                : state.renameRequest,
    };

    if (state.selectedClientKey !== clientKey) return withoutFile;

    const nextDisplayFiles = getSkillEditorDisplayFiles(withoutFile);
    const selection =
        nextDisplayFiles[displayIndex]?.clientKey ??
        nextDisplayFiles[displayIndex - 1]?.clientKey ??
        SKILL_INDEX_CLIENT_KEY;
    return { ...withoutFile, selectedClientKey: selection };
}

export function resetSkillEditorState(
    state: SkillEditorState,
    source: SkillEditorSource,
    options: ResetSkillEditorStateOptions = {}
): SkillEditorState {
    const replacement = createSkillEditorState(source, options);
    if (source.id !== state.skillId) {
        return {
            ...replacement,
            resetEpoch: state.resetEpoch + 1,
            editGeneration: state.editGeneration + 1,
        };
    }

    const selectedClientKey = stateContainsClientKey(
        replacement,
        state.selectedClientKey
    )
        ? state.selectedClientKey
        : SKILL_INDEX_CLIENT_KEY;
    const candidate = {
        ...replacement,
        selectedClientKey,
        resetEpoch: state.resetEpoch,
        editGeneration: state.editGeneration + 1,
        nextLocalFileKey: state.nextLocalFileKey,
    };
    const resetEpoch =
        selectedClientKey === state.selectedClientKey &&
        selectedContent(candidate) !== selectedContent(state)
            ? state.resetEpoch + 1
            : state.resetEpoch;

    return { ...candidate, resetEpoch };
}

export function reseedSkillEditorState(
    state: SkillEditorState,
    source: SkillEditorSource
): SkillEditorState {
    if (source.id !== state.skillId) {
        return resetSkillEditorState(state, source);
    }
    if (isSkillEditorDirty(state)) return state;
    return resetSkillEditorState(state, source, {
        reviewRequired: state.reviewRequired,
    });
}

export function discardSkillEditorChanges(
    state: SkillEditorState,
    source: SkillEditorSource = baselineSource(state)
): SkillEditorState {
    return resetSkillEditorState(state, source, { reviewRequired: false });
}

export function clearSkillEditorReviewRequired(
    state: SkillEditorState
): SkillEditorState {
    return state.reviewRequired ? { ...state, reviewRequired: false } : state;
}

export function getSkillEditorKey(state: SkillEditorState): string {
    return `${state.selectedClientKey}:${state.resetEpoch}`;
}
